import { CSSProperties } from 'react'

export interface ProgressBarProps {
    label?: string
    value?: number
    maxValue?: number
    displayValue?: string
    color?: 'accent' | 'default' | 'success' | 'warning' | 'danger'
    size?: 'sm' | 'md' | 'lg'
    showLabel?: boolean
    isIndeterminate?: boolean
    style?: CSSProperties
}

const colorMap: Record<string, string> = {
    accent: 'bg-accent',
    default: 'bg-default-foreground',
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
}

const heightMap = { sm: 'h-1', md: 'h-2', lg: 'h-3' }

export const ProgressBar = ({
    label,
    value = 0,
    maxValue = 100,
    displayValue,
    color = 'accent',
    size = 'md',
    showLabel = true,
    isIndeterminate = false,
    style,
}: ProgressBarProps) => {
    const percent = Math.min(Math.max((value / maxValue) * 100, 0), 100)
    const display = displayValue ?? (isIndeterminate ? undefined : `${Math.round(percent)}%`)

    return (
        <div
            role="progressbar"
            aria-valuenow={isIndeterminate ? undefined : value}
            aria-valuemin={0}
            aria-valuemax={maxValue}
            aria-label={label}
            className="flex flex-col gap-1"
            style={style}
        >
            {showLabel && (label || display) && (
                <div className="flex items-center justify-between">
                    {label && <span className="flex-1 pr-2 text-sm font-medium text-foreground">{label}</span>}
                    {display && <span className="whitespace-nowrap text-sm font-medium text-foreground">{display}</span>}
                </div>
            )}

            <div className={`relative w-full overflow-hidden rounded-xl bg-default ${heightMap[size]}`}>
                {isIndeterminate ? (
                    <div
                        className={`absolute inset-y-0 w-2/5 animate-[numeo-progress-indeterminate_1.5s_ease-in-out_infinite] rounded-xl ${colorMap[color]}`}
                    />
                ) : (
                    <div className={`h-full rounded-xl transition-[width] duration-300 ${colorMap[color]}`} style={{ width: `${percent}%` }} />
                )}
            </div>

            {isIndeterminate && (
                <style>{`
                    @keyframes numeo-progress-indeterminate {
                        0% { left: -40%; }
                        100% { left: 100%; }
                    }
                `}</style>
            )}
        </div>
    )
}
